import { t } from 'i18next';

import {
  SiteRateScheduleDTO,
  SiteRateScheduleOperations,
} from '~pages/Sales/Projects/components/SiteRatesForm';
import connection from '~services/connectionModule';

import { applyOrderRateReference } from './applyOrderRateReference';

type AddOnAssignments = SiteRateScheduleDTO['customerAddOns'];

interface OrderRateScheduleCopies {
  clonedAutoAddOnAssignments: Record<string, string>;
  clonedSiteRateSchedules: Record<string, string>;
}

/**
 * Redirects operations composed against the PROJECT's rate cards onto the order's own copies.
 *
 * Returns `null` when an id is neither a project record with a copy nor something the order
 * already owns: the card and assignment endpoints are id-scoped, so writing an unrecognised id
 * could hit the PROJECT's record — the exact leak the decoupling exists to prevent.
 *
 * Copies rather than mutates, because `buildSiteRateScheduleOperations` puts the rate manager's
 * own rows and add-on entries straight into the operations.
 */
const repointOperationsToCopies = (
  operations: SiteRateScheduleOperations,
  { clonedAutoAddOnAssignments, clonedSiteRateSchedules }: OrderRateScheduleCopies,
  orderOwnedIds: ReadonlySet<string>,
): SiteRateScheduleOperations | null => {
  let everyIdIsWritable = true;

  const copyOf = (copies: Record<string, string>, sourceId: string) => {
    const copyId = copies[sourceId];
    if (copyId) return copyId;
    // Already the order's own record — a copy from an earlier attempt in this drawer session, or
    // something this session's writes to the order created. Nothing to redirect.
    if (orderOwnedIds.has(sourceId)) return sourceId;

    everyIdIsWritable = false;

    return sourceId;
  };

  // A new assignment is POSTed under its card, so it has no id to redirect — but it must not
  // claim descent from a project assignment either, or deleting that project add-on would take
  // this decoupled order's copy with it. The backend severs the same link on the copies it mints.
  const repointAddOns = (addOns: AddOnAssignments): AddOnAssignments =>
    addOns?.map((assignment) =>
      assignment.isNew
        ? {
            ...assignment,
            addOn: assignment.addOn && {
              ...assignment.addOn,
              originalAutoAddOnAssignmentId: undefined,
            },
          }
        : assignment.addOn?.id
          ? {
              ...assignment,
              addOn: {
                ...assignment.addOn,
                id: copyOf(clonedAutoAddOnAssignments, assignment.addOn.id),
              },
            }
          : assignment,
    );

  const repointRate = (rate: SiteRateScheduleDTO): SiteRateScheduleDTO => ({
    ...rate,
    ...(rate.id ? { id: copyOf(clonedSiteRateSchedules, rate.id) } : {}),
    customerAddOns: repointAddOns(rate.customerAddOns),
    vendorAddOns: repointAddOns(rate.vendorAddOns),
    driverAddOns: repointAddOns(rate.driverAddOns),
  });

  const repointed: SiteRateScheduleOperations = {
    ...operations,
    // A created card carries no id, but a duplicated or copied one carries add-ons that still
    // name the project's assignment as their parent.
    toCreate: operations.toCreate.map(repointRate),
    toUpdate: operations.toUpdate.map((operation) => ({
      ...operation,
      rate: repointRate(operation.rate),
    })),
    // A row the user added and removed before saving has no id and no copy to find.
    toDelete: operations.toDelete.map((rate) =>
      rate.id ? { ...rate, id: copyOf(clonedSiteRateSchedules, rate.id) } : rate,
    ),
  };

  return everyIdIsWritable ? repointed : null;
};

interface DecoupleOrderFromProjectRatesArgs {
  companyId: string;
  operations: SiteRateScheduleOperations;
  orderId: string;
  // Records the order already owns, so a retry that re-emits them passes the redirect instead of
  // failing it. Empty on the first attempt of a drawer session.
  orderOwnedIds: ReadonlySet<string>;
}

/**
 * Takes an order permanently off its project's rate book and returns the pending rate-card
 * operations redirected onto the copies that produced — the save then writes them to the order
 * and never touches the project's cards.
 *
 * The order and its starred card MUST already be persisted, and the returned operations MUST be
 * issued after this resolves; `applyOrderRateReference` documents why each half of that ordering
 * is load-bearing.
 *
 * Returns `null` when the operations name a record that is neither copied nor already the
 * order's. The failure is already surfaced; the caller must abort the rate writes rather than
 * let them reach the project.
 */
export async function decoupleOrderFromProjectRates({
  companyId,
  operations,
  orderId,
  orderOwnedIds,
}: DecoupleOrderFromProjectRatesArgs): Promise<{
  clonedSiteRateSchedules: Record<string, string>;
  defaultSiteRateScheduleId: string | null;
  operations: SiteRateScheduleOperations;
  orderOwnedIds: string[];
} | null> {
  let copies: OrderRateScheduleCopies & { defaultSiteRateScheduleId: string | null };
  try {
    copies = await applyOrderRateReference({ companyId, orderId, dereferenced: true });
  } catch {
    // applyOrderRateReference already toasted and reported this.
    return null;
  }

  const copyIds = [
    ...Object.values(copies.clonedSiteRateSchedules),
    ...Object.values(copies.clonedAutoAddOnAssignments),
  ];

  const repointed = repointOperationsToCopies(
    operations,
    copies,
    new Set([...orderOwnedIds, ...copyIds]),
  );
  if (!repointed) {
    try {
      connection.handleRequestError(
        new Error('pending rate operations name a record the order does not own'),
        t('error_messages.orders.failed_to_stop_tracking_project_rates'),
      );
    } catch {
      // Error already handled (toast + Sentry)
    }

    return null;
  }

  return {
    clonedSiteRateSchedules: copies.clonedSiteRateSchedules,
    defaultSiteRateScheduleId: copies.defaultSiteRateScheduleId,
    operations: repointed,
    orderOwnedIds: copyIds,
  };
}
